/**
 * MainLayout — Portal CRM layout using @bloomcode/app-shell.
 *
 * Uses AppShell which provides:
 * - Universal header with app icon + name + Command Center trigger + profile
 * - Command Center slide-out panel with navigation grid
 * - CopyrightFooter
 */

import { useEffect, useCallback, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCookieSession } from '@bloomcode/auth';
import { useScopeContext } from '@/core/context';
import { useSessionManager, useWorkspaceEmbedded } from '@bloomcode/shared-modules';
import { setTenantContext } from '@/lib/apollo-client';
import { getAuthPortalLoginUrl, getAuthPortalLogoutUrl } from '@/config/env';
import { LoadingSpinner } from '@bloomcode/ui';
import { AppShell } from '@bloomcode/app-shell';
import type { NavItem, CommandCenterConfig, ExternalApp } from '@bloomcode/app-shell';
import {
  CrmIcon,
  CmsIcon,
  CommerceIcon,
  BillingIcon,
  SupportIcon,
  HrIcon,
  ProjectsIcon,
  ChatbotIcon,
  LedgerIcon,
} from '@bloomcode/icons';
import { useTheme } from '@bloomcode/theme';
import { useUpdateUserSettingsMutation, useGetActiveTenantModulesQuery } from '@bloomcode/graphql';
import {
  LayoutDashboard,
  MessageSquare,
  Handshake,
  Activity,
  FileText,
  ClipboardList,
  CalendarDays,
  Settings,
  Key,
  ExternalLink,
} from '@bloomcode/icons';
import { getAppUrl } from '@bloomcode/config';

interface MainLayoutProps {
  requireAuth?: boolean;
}

// Navigation items for the Command Center grid
const navItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/communications', label: 'Messages', icon: MessageSquare },
  { path: '/deals', label: 'My Deals', icon: Handshake },
  { path: '/activities', label: 'Activities', icon: Activity },
  { path: '/documents', label: 'Documents', icon: FileText },
  { path: '/forms', label: 'Forms', icon: ClipboardList },
  { path: '/meetings/request', label: 'Meeting', icon: CalendarDays },
  { path: '/preferences/notifications', label: 'Settings', icon: Settings },
];

const accountUrl = getAppUrl('portal-account');
const supportUrl = getAppUrl('portal-support');
const docsUrl = getAppUrl('portal-api-docs');

// Account portal URL for profile link
const accountPortalUrl = getAppUrl('portal-account');

// Command Center configuration — enables all sections
const commandCenterConfig: CommandCenterConfig = {
  quickSettings: true,
  help: [
    { label: 'Documentation', icon: FileText, url: docsUrl },
    {
      label: 'API Reference',
      icon: ExternalLink,
      url: `${docsUrl}/api`,
    },
    {
      label: 'Contact Support',
      icon: MessageSquare,
      url: `${supportUrl}/tickets/new`,
    },
  ],
  developerTools: [
    {
      label: 'API Keys',
      icon: Key,
      description: 'Manage your API keys',
      url: `${accountUrl}/api-keys`,
    },
    {
      label: 'Webhooks',
      icon: ExternalLink,
      description: 'Configure webhook endpoints',
      url: `${accountUrl}/webhooks`,
    },
    {
      label: 'API Documentation',
      icon: FileText,
      description: 'REST & GraphQL API docs',
      url: `${docsUrl}/api`,
    },
  ],
  ads: [
    {
      name: 'Bloomcode AI',
      description: 'Supercharge your CRM with AI-powered insights and automation.',
      ctaLabel: 'Learn More',
      ctaUrl: 'https://bloomcodetechnologies.com/ai',
      accentColor: '#6366f1',
    },
  ],
};

export default function MainLayout({ requireAuth = false }: MainLayoutProps) {
  const { isAuthenticated, isLoading, session, logout } = useCookieSession();
  const location = useLocation();
  const { setScope } = useScopeContext();
  const { theme, setTheme } = useTheme();
  const [updateUserSettings] = useUpdateUserSettingsMutation();
  const { isEmbedded } = useWorkspaceEmbedded();

  // Fetch active tenant modules to build app switcher (only for non-platform users)
  const { data: modulesData } = useGetActiveTenantModulesQuery({
    variables: { tenantId: session?.tenantId || '' },
    skip: !isAuthenticated || !session?.tenantId || session?.isPlatformAdmin === true,
    fetchPolicy: 'cache-first',
  });

  // Build app switcher based on user's scope level
  const externalApps: ExternalApp[] = useMemo(() => {
    const scopeLevel = session?.scopeLevel;
    const isPlatform = session?.isPlatformAdmin === true || scopeLevel === 'PLATFORM';
    const isApp = session?.isAppAdmin === true || scopeLevel === 'APP';

    // All available workspace apps (hosted-apps)
    const workspaceApps: ExternalApp[] = [
      {
        name: 'CRM',
        icon: CrmIcon,
        url: getAppUrl('app-crm'),
        category: 'workspace',
      },
      {
        name: 'CMS',
        icon: CmsIcon,
        url: getAppUrl('app-cms'),
        category: 'workspace',
      },
      {
        name: 'Commerce',
        icon: CommerceIcon,
        url: getAppUrl('app-commerce'),
        category: 'workspace',
      },
      {
        name: 'Projects',
        icon: ProjectsIcon,
        url: getAppUrl('app-projects'),
        category: 'workspace',
      },
      {
        name: 'Chatbot',
        icon: ChatbotIcon,
        url: getAppUrl('app-chatbot'),
        category: 'workspace',
      },
      {
        name: 'Billing',
        icon: BillingIcon,
        url: getAppUrl('app-billing'),
        category: 'workspace',
      },
      {
        name: 'Support',
        icon: SupportIcon,
        url: getAppUrl('portal-support'),
        category: 'workspace',
      },
      {
        name: 'HR',
        icon: HrIcon,
        url: getAppUrl('app-hr'),
        category: 'workspace',
      },
      {
        name: 'Books',
        icon: LedgerIcon,
        url: getAppUrl('portal-books'),
        category: 'workspace',
      },
    ];

    // Admin apps (only for PLATFORM and APP scope)
    const adminApps: ExternalApp[] = [
      {
        name: 'Platform Admin',
        icon: LayoutDashboard,
        url: getAppUrl('admin-platform'),
        category: 'admin',
      },
      {
        name: 'Tenant Admin',
        icon: Settings,
        url: getAppUrl('admin-tenant'),
        category: 'admin',
      },
    ];

    // Account/portal apps (always available)
    const accountApps: ExternalApp[] = [
      { name: 'Account', icon: Settings, url: accountUrl, category: 'account' },
      {
        name: 'Billing',
        icon: BillingIcon,
        url: getAppUrl('portal-billing'),
        category: 'account',
      },
      {
        name: 'Dashboard',
        icon: LayoutDashboard,
        url: getAppUrl('portal-dashboard'),
        category: 'account',
      },
    ];

    // PLATFORM scope: sees everything
    if (isPlatform) {
      return [...adminApps, ...workspaceApps, ...accountApps];
    }

    // APP scope: sees workspace + account (no admin)
    if (isApp) {
      return [...workspaceApps, ...accountApps];
    }

    // TENANT scope: only sees apps for their activated modules + account
    const modules = modulesData?.getActiveTenantModules?.data ?? [];
    const activeCodes = new Set(modules.map((m) => m.module?.code).filter(Boolean));

    const moduleToAppName: Record<string, string> = {
      CRM: 'CRM',
      CMS: 'CMS',
      COMMERCE: 'Commerce',
      PROJECTS: 'Projects',
      CHATBOT: 'Chatbot',
      BILLING: 'Billing',
      SUPPORT: 'Support',
      HR: 'HR',
      LEDGER: 'Books',
    };

    const tenantWorkspaceApps = workspaceApps.filter((app) => {
      const code = Object.entries(moduleToAppName).find(([, name]) => name === app.name)?.[0];
      return code && activeCodes.has(code);
    });

    return [...tenantWorkspaceApps, ...accountApps];
  }, [
    session?.scopeLevel,
    session?.isPlatformAdmin,
    session?.isAppAdmin,
    modulesData,
    session?.tenantId,
  ]);

  useSessionManager({
    enabled: requireAuth && isAuthenticated,
    onSessionExpired: () => {
      setTenantContext(null);
      window.location.href =
        getAuthPortalLogoutUrl(window.location.origin) + '?reason=session_expired';
    },
  });

  // Set tenant context from authenticated user
  useEffect(() => {
    if (isAuthenticated && session?.tenantId) {
      setScope({ tenantId: session?.tenantId });
      setTenantContext(session?.tenantId);
    } else {
      setTenantContext(null);
    }
  }, [isAuthenticated, session?.tenantId, setScope]);

  const handleLogout = useCallback(async () => {
    setTenantContext(null);
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    }
    const currentUrl = window.location.origin;
    const logoutUrl = getAuthPortalLogoutUrl(currentUrl);
    window.location.href = logoutUrl;
  }, [logout]);

  const handleSignIn = useCallback(() => {
    const returnUrl = window.location.href;
    window.location.href = getAuthPortalLoginUrl(returnUrl);
  }, []);

  const handleProfileClick = useCallback(() => {
    window.location.href = `${accountPortalUrl}/profile`;
  }, []);

  if (isLoading) {
    return <LoadingSpinner className="h-screen" />;
  }

  if (requireAuth && !isAuthenticated) {
    const returnUrl = window.location.origin + location.pathname + location.search;
    const authPortalLoginUrl = getAuthPortalLoginUrl(returnUrl);

    if (typeof window !== 'undefined') {
      window.location.href = authPortalLoginUrl;
    }

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Build user object for the header
  const user = session
    ? {
        email: session.email || '',
      }
    : undefined;

  // When embedded in workspace shell, render content only (no AppShell header/footer)
  if (isEmbedded) {
    return <Outlet />;
  }

  return (
    <>
      <Helmet>
        <title>Bloomcode CRM Portal</title>
        <meta
          name="description"
          content="Bloomcode CRM Portal - Manage your account and communications"
        />
      </Helmet>

      <AppShell
        app={{ name: 'Bloomcode CRM', icon: CrmIcon }}
        navigation={navItems}
        user={user}
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        onProfileClick={handleProfileClick}
        onSignIn={handleSignIn}
        commandCenter={{
          ...commandCenterConfig,
          externalApps,
          quickSettings: {
            enabled: true,
            currentTheme: theme as 'light' | 'dark' | 'system',
            onThemeChange: async (newTheme) => {
              setTheme(newTheme);
              try {
                await updateUserSettings({
                  variables: { input: { themePreference: newTheme.toUpperCase() } },
                });
              } catch {
                /* non-blocking — theme is already applied locally */
              }
            },
          },
        }}
        quickStatus={[
          { label: 'Open Tickets', value: 0, icon: MessageSquare, variant: 'default' },
          { label: 'Account', value: 'Active', icon: Activity, variant: 'success' },
        ]}
        quickActions={[
          {
            label: 'New Support Ticket',
            icon: MessageSquare,
            onClick: () => window.open(`${supportUrl}/tickets/new`, '_blank'),
          },
          { label: 'View Profile', icon: Settings, onClick: handleProfileClick },
        ]}
        footerAppName="Bloomcode CRM Portal"
      >
        <Outlet />
      </AppShell>
    </>
  );
}
